import { useContext, useEffect, useMemo, useState } from 'react';
import { Bookable, BookingDisplay } from '@/types';
import { formatDate } from '@/utils/formatDate';
import { formateTime } from '@/utils/formatTime';
import { getBookingDisplay } from '@/api/getBookingDisplay';
import { TDirection } from '../../data/direction';
import { getBookableDisplay } from '@/api/getBookableDisplay';
import { BookingSvgThemeContext } from '@/components/ThemeWrapper';

function svgLayout(fontSize: string | number, fallback: number) {
  const rawSize = typeof fontSize === 'number' ? fontSize : parseFloat(fontSize);
  const size = Number.isFinite(rawSize) ? rawSize : fallback;
  const height = Math.ceil(size * 1.5);
  return { viewBox: `0 0 1800 ${height}`, y: height / 2 };
}

interface BookingPageItemProps {
  bookable?: { bookableId: number; direction: TDirection };
  hideEndTime: boolean;
  timeThreshold: number;
  forcedTimeThreshold: number;
  hideName: boolean;
  hideUnbookedName?: boolean;
  hideExtra: boolean;
  hideTime: boolean;
  hideLocation: boolean;
  hideImage: boolean;
}

// const positions = [
//   { id: 'name', x: 0, y: 0, w: 12, h: 1 },
//   {
//     id: 'extra',
//     x: 0,
//     y: 1,
//     w: 12,
//     h: 1,
//   },
//   {
//     id: 'location',
//     x: 0,
//     y: 2,
//     w: 12,
//     h: 1,
//   },
//   {
//     id: 'time',
//     x: 0,
//     y: 3,
//     w: 12,
//     h: 1,
//   },
// ];

function BookingPageItem({
  bookable,
  hideEndTime,
  timeThreshold = 30,
  forcedTimeThreshold = 30,
  hideName,
  hideUnbookedName,
  hideExtra,
  hideTime,
  hideLocation,
  hideImage,
}: BookingPageItemProps) {
  const [bookings, setBookings] = useState<BookingDisplay[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const [fetchedBookable, setFetchedBookable] = useState<Bookable>();
  const { bookingNameFontSize, bookingUnbookedNameFontSize, bookingExtraFontSize, bookingLocationFontSize, bookingTimeFontSize } = useContext(BookingSvgThemeContext);
  const nameLayout = svgLayout(bookingNameFontSize, 100);
  const unbookedNameLayout = svgLayout(bookingUnbookedNameFontSize, 100);
  const extraLayout = svgLayout(bookingExtraFontSize, 40);
  const locationLayout = svgLayout(bookingLocationFontSize, 40);
  const timeLayout = svgLayout(bookingTimeFontSize, 40);
  const shouldHideUnbookedName = hideUnbookedName ?? false;

  async function getBookable() {
    try {
      const result = await getBookableDisplay({bookableId: bookable?.bookableId ?? 0})
      setFetchedBookable((result as any)?.data)
    }
    catch (error) {}
  }

  async function getBookings() {
    try {
      const response = await getBookingDisplay({ bookableId: bookable?.bookableId ?? 0 });
      setBookings((response as any)?.data);
    } catch (error) {}
  }

  useEffect(() => {
    getBookings();
    getBookable();

    const interval = setInterval(() => {
      getBookings();
    }, 24 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const currentBooking = useMemo(() => {
    const timeThresholdMs = timeThreshold * 60 * 1000; // 30 minutes in milliseconds
    const forcedTimeThresholdMs = forcedTimeThreshold * 60 * 1000; // 30 minutes in milliseconds

    const bookingsInsideThreshold = bookings?.filter(({ startTime }) => {
      const startTimeMs = +new Date(formatDate(startTime.toString())); // Parse UTC start time
      return startTimeMs - new Date().getTime() <= timeThresholdMs;
    });

    const sortedBookings = bookingsInsideThreshold.sort((a, b) => {
      const aStartTime = +new Date(formatDate(a.startTime.toString()));
      const bStartTime = +new Date(formatDate(b.startTime.toString()));

      if (aStartTime - bStartTime > forcedTimeThresholdMs) {
        return 1;
      }

      return -1;
    });

    return sortedBookings?.[0] ?? null;
  }, [bookings, timeThreshold, forcedTimeThreshold, currentTime]);

  if (!currentBooking) {
    return !shouldHideUnbookedName ? (
      <div className="booking-block w-full h-full relative p-6 flex items-center justify-center">
        <div className="booking-unbooked-name w-full">
          <svg width="100%" height="100%" viewBox={unbookedNameLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text x="50%" y={unbookedNameLayout.y} textAnchor="middle" dominantBaseline="middle" fontSize={bookingUnbookedNameFontSize}>
              {fetchedBookable?.name}
            </text>
          </svg>
        </div>
      </div>
    ) : null;
  }

  return (
    <div className="booking-block w-full h-full relative p-6">
      <div className="booking-name">
        {!hideName && (
          <svg width="100%" height="100%" viewBox={nameLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text x="50%" y={nameLayout.y} textAnchor="middle" dominantBaseline="middle" fontSize={bookingNameFontSize}>
              {currentBooking?.name ?? 'Namn'}
            </text>
          </svg>
        )}
      </div>
      <div className="booking-extra">
        {!hideExtra && (
          <svg width="100%" height="100%" viewBox={extraLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text x="50%" y={extraLayout.y} textAnchor="middle" dominantBaseline="middle" fontSize={bookingExtraFontSize}>
              {currentBooking?.extra}
            </text>
          </svg>
        )}
      </div>
      <div className="booking-location">
        {!hideLocation && (
          <svg width="100%" height="100%" viewBox={locationLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <text x="50%" y={locationLayout.y} textAnchor="middle" dominantBaseline="middle" fontSize={bookingLocationFontSize}>
              {currentBooking?.location}
            </text>
          </svg>
        )}
      </div>
      <div className="booking-time">
        <svg width="100%" height="100%" viewBox={timeLayout.viewBox} preserveAspectRatio="xMinYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
          <text x="50%" y={timeLayout.y} textAnchor="middle" dominantBaseline="middle" fontSize={bookingTimeFontSize}>
            {!hideTime && (
              <>
                {formateTime(new Date(formatDate(currentBooking?.startTime?.toString())).toString())}
                {hideEndTime ? '' : <> - {formateTime(new Date(formatDate(currentBooking?.endTime?.toString())).toString())}</>}
              </>
            )}
          </text>
        </svg>
      </div>
      {!hideImage && (
        <div className="booking-block__time mt-full absolute bottom-6 w-[20vw] h-[20vw] right-6">
          <svg width="100%" height="100%" viewBox={'0 0 24 24'} preserveAspectRatio="xMaxYMid meet" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
            <image href={currentBooking?.imageUrl ?? 'https://www.google.com/maps/d/thumbnail?mid=1'} x="0" y="0" height="100%" width="100%" />
          </svg>
        </div>
      )}
    </div>
  );
}

export default BookingPageItem;
